import { useAppDispatch, useAppSelector } from 'app/hooks';
import { SUN_DIRECTION_EDGE_TO_ORIENTATION } from 'data/constants';
import {
  ExposureDirectionType,
  FootprintPolygonsType,
  GridSpaceDataType
} from 'data/types';
import { useUpdateStructureOrientationMutation } from 'features/api/project-api';
import { setStructureOrientation } from 'features/structure/structure-thunks';
import { useCallback, useEffect, useRef, useState } from 'react';
import { refetchStructureSpaces } from 'utils/data';
import { getStructureOverlayBounds } from 'utils/structure-overlay-bounds';

const ORIENTATION_UPDATE_DEBOUNCE_MS = 500;
const SIDE_BOX_SIZE = 80;

type EdgeType = 'top' | 'right' | 'bottom' | 'left';

type SunDirectionOverlayProps = Readonly<{
  gridSpaces: GridSpaceDataType[];
  zoom: number;
  panOffset: { x: number; y: number };
  footprintPolygons?: FootprintPolygonsType | null;
}>;

export default function SunDirectionOverlay({
  gridSpaces,
  zoom,
  panOffset,
  footprintPolygons
}: SunDirectionOverlayProps) {
  const dispatch = useAppDispatch();
  const { projectSettings } = useAppSelector((state) => state.project);
  const { structureUniqueId } = useAppSelector((state) => state.structure);
  const [updateStructureOrientation] = useUpdateStructureOrientationMutation();

  const orientationSetting = projectSettings.find(
    (s) => s.code === 'structure_orientation'
  );
  const currentOrientation = orientationSetting?.value as
    | ExposureDirectionType
    | undefined;

  const [hasUserSelected, setHasUserSelected] = useState(false);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pendingOriginalOrientationRef = useRef<string | null>(null);
  const pendingNewOrientationRef = useRef<string | null>(null);

  const updateWallOrientations = useCallback(
    async (oldOrientation: string, newOrientation: string) => {
      if (!structureUniqueId) return;
      try {
        const result = await updateStructureOrientation({
          structureUniqueId,
          oldOrientation,
          newOrientation
        }).unwrap();
        if (result.affectedSpaceUniqueIds.length > 0) {
          await refetchStructureSpaces(
            result.affectedSpaceUniqueIds,
            structureUniqueId
          );
        }
      } catch (error) {
        console.error('Failed to update wall orientations:', error);
      }
    },
    [structureUniqueId, updateStructureOrientation]
  );

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  const handleSideClick = useCallback(
    (edge: EdgeType, e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const newOrientation = SUN_DIRECTION_EDGE_TO_ORIENTATION[edge];
      if (!newOrientation) return;

      setHasUserSelected(true);
      const oldOrientation = currentOrientation ?? 'North';

      dispatch(
        setStructureOrientation({
          oldOrientation,
          newOrientation
        })
      );

      if (pendingOriginalOrientationRef.current === null) {
        pendingOriginalOrientationRef.current = oldOrientation;
      }

      pendingNewOrientationRef.current = newOrientation;

      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      debounceTimeoutRef.current = setTimeout(() => {
        const original = pendingOriginalOrientationRef.current;
        const final = pendingNewOrientationRef.current;
        pendingOriginalOrientationRef.current = null;
        pendingNewOrientationRef.current = null;
        if (original && final) {
          updateWallOrientations(original, final);
        }
      }, ORIENTATION_UPDATE_DEBOUNCE_MS);
    },
    [currentOrientation, dispatch, updateWallOrientations]
  );

  const activeEdge = (
    Object.entries(SUN_DIRECTION_EDGE_TO_ORIENTATION) as [
      EdgeType,
      ExposureDirectionType
    ][]
  ).find(([, orientation]) => orientation === currentOrientation)?.[0];

  const defaultStyle =
    'absolute flex items-center justify-center rounded border-2 border-amber-400 bg-amber-100/80 text-sm font-medium text-amber-900 transition-all hover:bg-amber-200/90 hover:border-amber-500 cursor-pointer pointer-events-auto';

  const getBoxStyle = (edge: EdgeType) => {
    if (!hasUserSelected || !activeEdge) return defaultStyle;
    const base =
      'absolute flex items-center justify-center rounded text-sm font-medium transition-all cursor-pointer pointer-events-auto';
    if (edge === activeEdge) {
      return `${base} border-[3px] border-amber-500 bg-amber-100/80 text-amber-900 hover:bg-amber-200/90 hover:border-amber-600`;
    }
    return `${base} border-2 border-amber-300 bg-amber-50/60 text-amber-700 opacity-40 hover:opacity-70 hover:bg-amber-100/70 hover:border-amber-400`;
  };

  const bounds = getStructureOverlayBounds(gridSpaces, footprintPolygons);
  if (!bounds) return null;

  const { minX, minY, maxX, maxY } = bounds;

  return (
    <div className='pointer-events-none absolute inset-0 z-10' aria-hidden>
      <div
        className='pointer-events-auto absolute inset-0'
        style={{
          transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${zoom})`,
          transformOrigin: '0 0'
        }}
      >
        <button
          type='button'
          id='sun-direction-top'
          className={getBoxStyle('top')}
          style={{
            left: minX,
            top: minY - SIDE_BOX_SIZE,
            width: maxX - minX,
            height: SIDE_BOX_SIZE
          }}
          onClick={(e) => handleSideClick('top', e)}
        >
          Afternoon Sun
        </button>
        <button
          type='button'
          id='sun-direction-right'
          className={getBoxStyle('right')}
          style={{
            left: maxX,
            top: minY,
            width: SIDE_BOX_SIZE,
            height: maxY - minY
          }}
          onClick={(e) => handleSideClick('right', e)}
        >
          Afternoon Sun
        </button>
        <button
          type='button'
          id='sun-direction-bottom'
          className={getBoxStyle('bottom')}
          style={{
            left: minX,
            top: maxY,
            width: maxX - minX,
            height: SIDE_BOX_SIZE
          }}
          onClick={(e) => handleSideClick('bottom', e)}
        >
          Afternoon Sun
        </button>
        <button
          type='button'
          id='sun-direction-left'
          className={getBoxStyle('left')}
          style={{
            left: minX - SIDE_BOX_SIZE,
            top: minY,
            width: SIDE_BOX_SIZE,
            height: maxY - minY
          }}
          onClick={(e) => handleSideClick('left', e)}
        >
          Afternoon Sun
        </button>
      </div>
    </div>
  );
}
